<template>
  <div class="min-h-screen">
    <!-- Loading -->
    <div v-if="loading" class="flex justify-center items-center h-48">
      <div class="text-center">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
        <p class="mt-3 text-xs text-gray-500">Chargement du ticket...</p>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">Erreur</h3>
          <div class="mt-2 text-sm text-red-700">{{ error }}</div>
          <div class="mt-4">
            <router-link
              to="/support/tickets"
              class="text-sm font-medium text-red-600 hover:text-red-500"
            >
              Retour à la liste des tickets
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div v-else-if="ticket" class="max-w-full">
      <!-- Header Compact -->
      <div class="mb-3 bg-white shadow-sm rounded-lg px-4 py-3">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <router-link
              to="/support/tickets"
              class="text-gray-400 hover:text-gray-600"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </router-link>
            <div>
              <div class="flex items-center space-x-2">
                <h1 class="text-lg font-bold text-gray-900">
                  #{{ ticket.tid }}
                </h1>
                <span class="text-sm text-gray-600">{{ ticket.subject }}</span>
                <!-- Indicateur de mise à jour en temps réel -->
                <span class="inline-flex items-center text-xs text-gray-400">
                  <svg class="h-3 w-3 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                    <circle cx="10" cy="10" r="3" />
                  </svg>
                  <span class="ml-1">Live</span>
                </span>
              </div>
              <div class="mt-0.5 flex items-center space-x-3 text-xs text-gray-500">
                <span class="flex items-center">
                  <svg class="h-3.5 w-3.5 mr-1 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                  </svg>
                  {{ ticket.requestor_name || ticket.name }} ({{ ticket.requestor_email || ticket.email }})
                </span>
                <span v-if="ticket.requestor_type === 'Authorized User'" class="inline-flex items-center px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs">
                  Sous-utilisateur
                </span>
                <span class="flex items-center">
                  <svg class="h-3.5 w-3.5 mr-1 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd" />
                  </svg>
                  {{ formatDate(ticket.date) }}
                </span>
                <span class="flex items-center">
                  <svg class="h-3.5 w-3.5 mr-1 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                  </svg>
                  {{ ticket.department }}
                </span>
              </div>
            </div>
          </div>

          <div class="flex items-center space-x-2">
            <TicketStatusBadge :status="ticket.status" />
            <TicketPriorityBadge :priority="ticket.priority" />
          </div>
        </div>
      </div>

      <!-- Main Layout: 3 columns -->
      <div class="grid grid-cols-1 xl:grid-cols-5 gap-3">
        <!-- Left Column: Editor + Conversation (2/5) -->
        <div class="xl:col-span-2 space-y-3">
          <!-- Editor -->
          <div v-if="ticket" class="bg-white shadow-sm rounded-lg overflow-hidden">
            <div class="px-3 py-2 border-b border-gray-200 bg-gray-50">
              <h2 class="text-xs font-medium text-gray-700 uppercase tracking-wide">
                Répondre
              </h2>
            </div>
            <div class="p-0">
              <RichMarkdownEditor
                ref="richEditor"
                v-model="editorContent"
                :ticket-id="id"
                :support-service="supportService"
                :default-template="defaultMessage"
                @success="handleReplySuccess"
                @error="handleReplyError"
                @cancel="editorContent = ''"
              />
            </div>
          </div>

          <!-- Related Item -->
          <div v-if="relatedItem" class="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            <div class="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <h3 class="text-xs font-medium text-gray-700 uppercase tracking-wide">
                {{ relatedItem.type === 'product' ? 'Produit Concerné' : 'Domaine Concerné' }}
              </h3>
              <a
                v-if="relatedItem.type === 'product'"
                :href="`https://customer.onetsolutions.net/administration/clientsservices.php?userid=${ticket.userid}&id=${relatedItem.id}`"
                target="_blank"
                class="inline-flex items-center text-xs text-gray-600 hover:text-gray-900 transition-colors"
                title="Ouvrir dans WHMCS"
              >
                <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <a
                v-else-if="relatedItem.type === 'domain'"
                :href="`https://customer.onetsolutions.net/administration/clientsdomains.php?userid=${ticket.userid}&id=${relatedItem.id}`"
                target="_blank"
                class="inline-flex items-center text-xs text-gray-600 hover:text-gray-900 transition-colors"
                title="Ouvrir dans WHMCS"
              >
                <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
            <div class="px-3 py-2.5 space-y-2">
              <!-- Product/Domain Name -->
              <div>
                <a
                  v-if="relatedItem.type === 'product'"
                  :href="`https://customer.onetsolutions.net/administration/clientsservices.php?userid=${ticket.userid}&id=${relatedItem.id}`"
                  target="_blank"
                  class="text-xs font-semibold text-gray-900 hover:text-gray-700 transition-colors inline-flex items-center gap-1.5"
                >
                  {{ relatedItem.name }}
                  <svg class="h-3.5 w-3.5 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <a
                  v-else-if="relatedItem.type === 'domain'"
                  :href="`https://customer.onetsolutions.net/administration/clientsdomains.php?userid=${ticket.userid}&id=${relatedItem.id}`"
                  target="_blank"
                  class="text-xs font-semibold text-gray-900 hover:text-gray-700 transition-colors inline-flex items-center gap-1.5"
                >
                  {{ relatedItem.name }}
                  <svg class="h-3.5 w-3.5 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <p v-else class="text-xs font-semibold text-gray-900">
                  {{ relatedItem.name }}
                </p>
                <p v-if="relatedItem.domain" class="text-xs text-gray-500 mt-0.5">
                  {{ relatedItem.domain }}
                </p>
              </div>

              <!-- Dedicated IP (highlighted if available) -->
              <div v-if="relatedItem.dedicatedip" class="border border-gray-300 rounded px-2 py-1.5">
                <dt class="text-xs text-gray-500 font-medium mb-0.5">IP Dédiée</dt>
                <dd class="text-sm text-gray-900 font-mono font-semibold">{{ relatedItem.dedicatedip }}</dd>
              </div>

              <!-- Status and Key Info -->
              <div class="flex items-center gap-2 flex-wrap">
                <span :class="[
                  'inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium',
                  relatedItem.status === 'Active' ? 'bg-green-50 text-green-700 border border-green-200' :
                  relatedItem.status === 'Suspended' ? 'bg-red-50 text-red-700 border border-red-200' :
                  relatedItem.status === 'Pending' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                  'bg-gray-50 text-gray-700 border border-gray-200'
                ]">
                  {{ relatedItem.status }}
                </span>
                <span v-if="relatedItem.groupname" class="inline-flex items-center px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200 text-xs">
                  {{ relatedItem.groupname }}
                </span>
              </div>

              <!-- Product Details Grid -->
              <dl class="grid grid-cols-2 gap-x-2 gap-y-1.5 text-xs">

                <!-- Pricing -->
                <div v-if="relatedItem.amount">
                  <dt class="text-gray-500 font-medium">Prix</dt>
                  <dd class="text-gray-900 font-semibold">{{ relatedItem.amount }}€</dd>
                </div>

                <!-- Billing Cycle -->
                <div v-if="relatedItem.billingcycle">
                  <dt class="text-gray-500 font-medium">Cycle</dt>
                  <dd class="text-gray-900">{{ relatedItem.billingcycle }}</dd>
                </div>

                <!-- Registration Date -->
                <div v-if="relatedItem.regdate" class="col-span-2">
                  <dt class="text-gray-500 font-medium">Date de création</dt>
                  <dd class="text-gray-900">{{ formatDate(relatedItem.regdate) }}</dd>
                </div>

                <!-- Next Due Date (highlighted) -->
                <div v-if="relatedItem.nextduedate" class="col-span-2 bg-blue-50 border border-blue-200 rounded px-2 py-1 -mx-2">
                  <dt class="text-blue-700 font-medium text-xs mb-0.5">Prochain paiement</dt>
                  <dd class="text-blue-900 font-semibold">{{ formatDate(relatedItem.nextduedate) }}</dd>
                </div>

                <!-- Expiry Date (for domains - highlighted) -->
                <div v-if="relatedItem.expiry_date" class="col-span-2 bg-orange-50 border border-orange-200 rounded px-2 py-1 -mx-2">
                  <dt class="text-orange-700 font-medium text-xs mb-0.5">Date d'expiration</dt>
                  <dd class="text-orange-900 font-semibold">{{ formatDate(relatedItem.expiry_date) }}</dd>
                </div>

                <!-- Server Name -->
                <div v-if="relatedItem.servername" class="col-span-2">
                  <dt class="text-gray-500 font-medium">Serveur</dt>
                  <dd class="text-gray-900 font-mono text-xs">{{ relatedItem.servername }}</dd>
                </div>

                <!-- Username -->
                <div v-if="relatedItem.username" class="col-span-2">
                  <dt class="text-gray-500 font-medium">Utilisateur</dt>
                  <dd class="text-gray-900 font-mono">{{ relatedItem.username }}</dd>
                </div>

                <!-- Nameservers (for domains) -->
                <div v-if="relatedItem.ns1 || relatedItem.ns2" class="col-span-2">
                  <dt class="text-gray-500 font-medium mb-0.5">Nameservers</dt>
                  <dd class="text-gray-900 font-mono text-xs space-y-0.5">
                    <div v-if="relatedItem.ns1">{{ relatedItem.ns1 }}</div>
                    <div v-if="relatedItem.ns2">{{ relatedItem.ns2 }}</div>
                  </dd>
                </div>

                <!-- Registration Period (for domains) -->
                <div v-if="relatedItem.registrationperiod">
                  <dt class="text-gray-500 font-medium">Période</dt>
                  <dd class="text-gray-900">{{ relatedItem.registrationperiod }} an(s)</dd>
                </div>

                <!-- Config Options (if any) -->
                <div v-if="relatedItem.configoptions" class="col-span-2">
                  <dt class="text-gray-500 font-medium mb-1">Options</dt>
                  <dd class="space-y-0.5">
                    <div v-for="(value, key) in relatedItem.configoptions" :key="key" class="text-gray-900 text-xs">
                      <span class="font-medium">{{ key }}:</span> {{ value }}
                    </div>
                  </dd>
                </div>
              </dl>

              <!-- Notes (if any) -->
              <div v-if="relatedItem.notes" class="pt-2 border-t border-gray-200">
                <dt class="text-gray-500 font-medium text-xs mb-1">Notes</dt>
                <dd class="text-gray-700 text-xs">{{ relatedItem.notes }}</dd>
              </div>
            </div>
          </div>

          <!-- Conversation -->
          <div class="bg-white shadow-sm rounded-lg overflow-hidden">
            <div class="px-3 py-2 border-b border-gray-200 bg-gray-50">
              <h2 class="text-xs font-medium text-gray-700 uppercase tracking-wide">Historique</h2>
            </div>
            <div class="px-3 py-3 space-y-3 max-h-[600px] overflow-y-auto">
              <!-- All Messages (sorted by date descending - most recent first) -->
              <div
                v-for="message in allMessages"
                :key="message.id || message.replyid"
                :class="['flex', message.admin ? 'justify-end' : 'justify-start']"
              >
                <div :class="['flex gap-2 max-w-full', message.admin ? 'flex-row-reverse' : 'flex-row']">
                  <!-- Avatar -->
                  <img
                    :src="getGravatarUrl(message.email)"
                    :alt="message.name"
                    class="w-7 h-7 rounded-full flex-shrink-0 ring-1 ring-gray-200"
                  />
                  <!-- Message -->
                  <div :class="['flex-1 min-w-0']">
                    <div :class="['flex items-baseline gap-1.5 mb-0.5 flex-wrap', message.admin ? 'justify-end' : 'justify-start']">
                      <span class="text-xs font-medium text-gray-900">{{ message.requestor_name || message.name }}</span>
                      <span
                        v-if="message.admin"
                        class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-900 text-white"
                      >
                        Support
                      </span>
                      <span
                        v-if="!message.admin && message.requestor_type === 'Authorized User'"
                        class="inline-flex items-center px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs"
                      >
                        Sous-utilisateur
                      </span>
                      <span class="text-xs text-gray-400">{{ formatDate(message.date) }}</span>
                    </div>
                    <div v-if="!message.admin && message.requestor_type === 'Authorized User'" class="text-xs text-gray-500 mb-1">
                      {{ message.requestor_email }}
                    </div>
                    <div
                      :class="[
                        'rounded-lg px-3 py-2 border',
                        message.admin
                          ? 'bg-gray-900 text-white border-gray-900'
                          : 'bg-white text-gray-900 border-gray-200'
                      ]"
                    >
                      <div class="prose prose-sm max-w-none text-xs break-words" :class="message.admin ? 'prose-invert' : ''" v-html="renderMarkdown(message.message)"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Internal Notes -->
          <div v-if="notes && notes.length > 0" class="bg-white shadow-sm rounded-lg overflow-hidden">
            <div class="px-3 py-2 border-b border-gray-200 bg-gray-50">
              <h2 class="text-xs font-medium text-gray-700 uppercase tracking-wide">Notes internes</h2>
            </div>
            <div class="divide-y divide-gray-200">
              <div
                v-for="note in notes"
                :key="note.id"
                class="px-3 py-2"
              >
                <div class="flex items-center justify-between mb-1">
                  <span class="text-xs font-medium text-gray-900">{{ note.admin }}</span>
                  <span class="text-xs text-gray-500">{{ formatDate(note.date) }}</span>
                </div>
                <div class="prose prose-sm max-w-none text-xs text-gray-600" v-html="renderMarkdown(note.message)"></div>
                <div v-if="note.attachment" class="mt-1.5 text-xs text-gray-500">
                  <span class="inline-flex items-center">
                    <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    {{ note.attachment }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Middle Column: Client Context (2/5) -->
        <div class="xl:col-span-2 space-y-3">
          <ContexteClient
            :context="clientContext"
            :loading="contextLoading"
            :error="contextError"
          />
        </div>

        <!-- Right Column: KB Suggestions (1/5) -->
        <div class="xl:col-span-1 space-y-3">
          <SuggestionsKB
            :suggestions="kbSuggestions"
            :loading="kbLoading"
            :error="kbError"
            @refresh="loadKBSuggestions"
            @use-response="useKBResponse"
            @insert-snippet="insertKBSnippet"
            @update-suggestion="updateKBSuggestion"
          />
        </div>
      </div>

      <!-- Success Message -->
      <div
        v-if="successMessage"
        class="fixed bottom-4 right-4 max-w-md bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-50"
      >
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <svg class="h-4 w-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-2 flex-1">
            <p class="text-xs font-medium text-gray-900">{{ successMessage }}</p>
          </div>
          <button @click="successMessage = null" class="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600">
            <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="errorMessage"
        class="fixed bottom-4 right-4 max-w-md bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-50"
      >
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <svg class="h-4 w-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-2 flex-1">
            <p class="text-xs font-medium text-gray-900">{{ errorMessage }}</p>
          </div>
          <button @click="errorMessage = null" class="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600">
            <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createSupportService } from '@/composables/supportService';
import { getAccessTokenSilently } from '@/auth';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { marked } from 'marked';

import TicketStatusBadge from '@/components/support/TicketStatusBadge.vue';
import TicketPriorityBadge from '@/components/support/TicketPriorityBadge.vue';
import ContexteClient from '@/components/support/ContexteClient.vue';
import SuggestionsKB from '@/components/support/SuggestionsKB.vue';
import RichMarkdownEditor from '@/components/support/RichMarkdownEditor.vue';

export default {
  name: 'TicketDetailView',
  components: {
    TicketStatusBadge,
    TicketPriorityBadge,
    ContexteClient,
    SuggestionsKB,
    RichMarkdownEditor
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      ticket: null,
      replies: [],
      initialMessage: null,
      notes: [],
      relatedItem: null,
      clientContext: null,
      kbSuggestions: [],
      loading: false,
      contextLoading: false,
      kbLoading: false,
      error: null,
      contextError: null,
      kbError: null,
      editorContent: '',
      successMessage: null,
      errorMessage: null,
      supportService: null,
      assignedAgent: null,
      refreshInterval: null
    };
  },
  watch: {
    ticket(newTicket) {
      // Mettre à jour le titre de la page quand le ticket est chargé
      if (newTicket) {
        this.updatePageTitle(newTicket.tid);
      }
    }
  },
  computed: {
    defaultMessage() {
      if (!this.ticket || !this.assignedAgent) return '';
      // Utiliser requestor_name en priorité, puis name
      const fullName = this.ticket.requestor_name || this.ticket.name || 'Client';
      // Extraire uniquement le prénom (premier mot)
      const firstName = fullName.split(' ')[0];
      const agentName = this.assignedAgent || 'L\'équipe support';
      return `Bonjour ${firstName},\n\n\n\nCordialement,\n${agentName}`;
    },
    allMessages() {
      // Combiner le message initial et les réponses, puis trier par date décroissante
      const messages = [];
      if (this.initialMessage) {
        messages.push(this.initialMessage);
      }
      if (this.replies && this.replies.length > 0) {
        messages.push(...this.replies);
      }
      // Trier par date décroissante (plus récent en premier)
      return messages.sort((a, b) => {
        const dateA = new Date(a.date.replace(' ', 'T'));
        const dateB = new Date(b.date.replace(' ', 'T'));
        return dateB - dateA;
      });
    }
  },
  async mounted() {
    // Configurer marked pour interpréter les sauts de ligne correctement
    marked.setOptions({
      breaks: false,    // Ne pas convertir les simples \n en <br>, garder le comportement markdown standard
      gfm: true,        // GitHub Flavored Markdown
      pedantic: false   // Ne pas être strict sur le markdown
    });

    this.supportService = createSupportService(getAccessTokenSilently);

    // Charger le ticket et les suggestions KB en parallèle pour optimiser le chargement
    await Promise.all([
      this.loadTicket(),
      this.loadKBSuggestions()
    ]);

    // Rafraîchissement automatique toutes les 10 secondes
    this.refreshInterval = setInterval(() => {
      this.loadTicket(false); // false = pas de loader
    }, 10000);
  },
  beforeUnmount() {
    // Nettoyer l'intervalle de rafraîchissement
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    // Restaurer le titre par défaut
    document.title = 'Console Clafoutis';
  },
  methods: {
    async loadTicket(showLoader = true) {
      if (showLoader) {
        this.loading = true;
        this.contextLoading = true;
      }
      this.error = null;

      try {
        const response = await this.supportService.getTicket(this.id);

        if (response.success) {
          this.ticket = response.ticket;

          // Extraire le nom de l'agent assigné
          this.assignedAgent = response.ticket?.assigned_agent || response.assigned_agent || null;

          // Extraire les réponses de la structure WHMCS
          let rawReplies = response.replies || response.ticket?.replies;

          // Les réponses peuvent être dans différents formats
          let allReplies = [];
          if (Array.isArray(rawReplies) && rawReplies.length > 0 && rawReplies[0].reply) {
            allReplies = rawReplies[0].reply;
          } else if (rawReplies && rawReplies.reply) {
            allReplies = Array.isArray(rawReplies.reply) ? rawReplies.reply : [rawReplies.reply];
          } else if (Array.isArray(rawReplies)) {
            allReplies = rawReplies;
          }

          // Extraire le message initial (replyid = "0") et les autres réponses
          this.initialMessage = allReplies.find(reply => reply.replyid === "0");
          this.replies = allReplies.filter(reply => reply.replyid !== "0");

          // Extraire les notes internes
          this.notes = response.notes || [];

          // Extraire le related_item (produit ou domaine lié)
          this.relatedItem = response.related_item || null;

          // Désactiver le loader principal dès que les données essentielles sont chargées
          if (showLoader) {
            this.loading = false;
          }

          // Initialiser l'éditeur avec le message par défaut si vide (uniquement au premier chargement)
          if (!this.editorContent && this.assignedAgent && showLoader) {
            this.$nextTick(() => {
              this.editorContent = this.defaultMessage;
            });
          }

          // Construire le contexte client (peut prendre plus de temps)
          if (response.client_context || response.client) {
            this.clientContext = {
              client: response.client_context?.client || response.client || null,
              products: response.client_context?.products || [],
              domains: response.client_context?.domains || [],
              recent_invoices: response.client_context?.recent_invoices || [],
              tickets_history: response.client_context?.tickets_history || [],
              ai_summary: response.client_context?.ai_summary || response.client_context?.ai_context_summary || null,
              client_stats: response.client_stats || null,
              ticket: response.ticket,
              success: true
            };
          }

          console.log('Ticket loaded:', {
            initialMessage: this.initialMessage,
            replies: this.replies.length,
            notes: this.notes.length,
            relatedItem: this.relatedItem
          });
          console.log('Client context loaded:', this.clientContext);
        } else {
          this.error = response.message || 'Erreur lors du chargement du ticket';
        }
      } catch (err) {
        console.error('Error loading ticket:', err);
        this.error = err.response?.data?.message || 'Erreur lors du chargement du ticket';
      } finally {
        if (showLoader) {
          this.loading = false;
          this.contextLoading = false;
        }
      }
    },

    async loadKBSuggestions() {
      this.kbLoading = true;
      this.kbError = null;

      try {
        const response = await this.supportService.getSuggestedKBResponses(this.id);

        if (response.success) {
          this.kbSuggestions = response.suggestions || [];
        } else {
          this.kbError = response.message || 'Erreur lors du chargement des suggestions';
        }
      } catch (err) {
        console.error('Error loading KB suggestions:', err);
        this.kbError = err.response?.data?.message || 'Erreur lors du chargement des suggestions';
      } finally {
        this.kbLoading = false;
      }
    },

    handleReplySuccess(response) {
      this.successMessage = 'Réponse envoyée';

      // Réinitialiser avec le message par défaut
      this.editorContent = this.defaultMessage;

      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        this.successMessage = null;
      }, 3000);

      // Reload ticket de manière transparente (pas de loader)
      this.loadTicket(false);
    },

    handleReplyError(message) {
      this.errorMessage = message;

      // Auto-hide error message after 10 seconds
      setTimeout(() => {
        this.errorMessage = null;
      }, 10000);
    },


    useKBResponse(content, kbId = null) {
      // Utiliser la méthode publique de l'éditeur pour insérer du markdown
      if (this.$refs.richEditor) {
        // Ajouter le bonjour et la signature
        const fullName = this.ticket?.requestor_name || this.ticket?.name || 'Client';
        const firstName = fullName.split(' ')[0];
        const agentName = this.assignedAgent || 'L\'équipe support';

        const formattedContent = `Bonjour ${firstName},\n\n${content}\n\nCordialement,\n${agentName}`;

        this.$refs.richEditor.setMarkdownContent(formattedContent);
        // Si on a un ID KB, le sauvegarder pour permettre la mise à jour
        if (kbId) {
          this.$refs.richEditor.kbSourceId = kbId;
        }
        this.$nextTick(() => {
          this.$refs.richEditor.focus();
          // Scroll to editor
          const editorElement = this.$refs.richEditor.$el;
          if (editorElement) {
            editorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    },

    insertKBSnippet(content, kbId = null) {
      // Insérer le snippet à la position actuelle dans l'éditeur
      if (this.$refs.richEditor) {
        // Si l'éditeur est vide, ajouter le bonjour et la signature
        const editorContent = this.$refs.richEditor.content || '';
        const isEditorEmpty = !editorContent.trim();

        if (isEditorEmpty) {
          const fullName = this.ticket?.requestor_name || this.ticket?.name || 'Client';
          const firstName = fullName.split(' ')[0];
          const agentName = this.assignedAgent || 'L\'équipe support';

          const formattedContent = `Bonjour ${firstName},\n\n${content}\n\nCordialement,\n${agentName}`;
          this.$refs.richEditor.setMarkdownContent(formattedContent);
        } else {
          // Sinon, insérer simplement le contenu
          this.$refs.richEditor.insertMarkdownContent('\n\n' + content, kbId);
        }

        if (kbId) {
          this.$refs.richEditor.kbSourceId = kbId;
        }

        this.$nextTick(() => {
          this.$refs.richEditor.focus();
          // Scroll to editor
          const editorElement = this.$refs.richEditor.$el;
          if (editorElement) {
            editorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    },

    async updateKBSuggestion(id, payload) {
      try {
        const response = await this.supportService.updateKBResponse(id, payload);

        if (response.success) {
          // Recharger les suggestions KB pour afficher les modifications
          await this.loadKBSuggestions();

          // Afficher un message de succès
          this.successMessage = 'Réponse KB mise à jour';
          setTimeout(() => {
            this.successMessage = null;
          }, 3000);
        } else {
          this.errorMessage = response.message || 'Erreur lors de la mise à jour';
          setTimeout(() => {
            this.errorMessage = null;
          }, 5000);
        }
      } catch (error) {
        console.error('Error updating KB suggestion:', error);
        this.errorMessage = error.response?.data?.message || 'Erreur lors de la mise à jour';
        setTimeout(() => {
          this.errorMessage = null;
        }, 5000);
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = typeof dateString === 'string' && dateString.includes(' ')
          ? parseISO(dateString.replace(' ', 'T'))
          : parseISO(dateString);
        return format(date, 'dd MMM yyyy HH:mm', { locale: fr });
      } catch (e) {
        return dateString;
      }
    },

    renderMarkdown(text) {
      if (!text) return '';
      return marked(text);
    },

    getInitials(name) {
      if (!name) return '?';
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },

    getGravatarUrl(email) {
      if (!email) return 'https://www.gravatar.com/avatar/?d=mp';

      // Créer un hash MD5 de l'email (simplifié pour Vue)
      const hash = this.md5(email.toLowerCase().trim());
      return `https://www.gravatar.com/avatar/${hash}?d=mp&s=80`;
    },

    updatePageTitle(ticketId) {
      if (ticketId) {
        document.title = `Ticket #${ticketId} - Console Clafoutis`;
      } else {
        document.title = 'Ticket Support - Console Clafoutis';
      }
    },

    md5(string) {
      // Implémentation simplifiée de MD5 pour Gravatar
      // En production, utilisez une vraie lib MD5 ou crypto-js
      function md5cycle(x, k) {
        let a = x[0], b = x[1], c = x[2], d = x[3];
        a = ff(a, b, c, d, k[0], 7, -680876936);
        d = ff(d, a, b, c, k[1], 12, -389564586);
        c = ff(c, d, a, b, k[2], 17, 606105819);
        b = ff(b, c, d, a, k[3], 22, -1044525330);
        a = ff(a, b, c, d, k[4], 7, -176418897);
        d = ff(d, a, b, c, k[5], 12, 1200080426);
        c = ff(c, d, a, b, k[6], 17, -1473231341);
        b = ff(b, c, d, a, k[7], 22, -45705983);
        a = ff(a, b, c, d, k[8], 7, 1770035416);
        d = ff(d, a, b, c, k[9], 12, -1958414417);
        c = ff(c, d, a, b, k[10], 17, -42063);
        b = ff(b, c, d, a, k[11], 22, -1990404162);
        a = ff(a, b, c, d, k[12], 7, 1804603682);
        d = ff(d, a, b, c, k[13], 12, -40341101);
        c = ff(c, d, a, b, k[14], 17, -1502002290);
        b = ff(b, c, d, a, k[15], 22, 1236535329);
        a = gg(a, b, c, d, k[1], 5, -165796510);
        d = gg(d, a, b, c, k[6], 9, -1069501632);
        c = gg(c, d, a, b, k[11], 14, 643717713);
        b = gg(b, c, d, a, k[0], 20, -373897302);
        a = gg(a, b, c, d, k[5], 5, -701558691);
        d = gg(d, a, b, c, k[10], 9, 38016083);
        c = gg(c, d, a, b, k[15], 14, -660478335);
        b = gg(b, c, d, a, k[4], 20, -405537848);
        a = gg(a, b, c, d, k[9], 5, 568446438);
        d = gg(d, a, b, c, k[14], 9, -1019803690);
        c = gg(c, d, a, b, k[3], 14, -187363961);
        b = gg(b, c, d, a, k[8], 20, 1163531501);
        a = gg(a, b, c, d, k[13], 5, -1444681467);
        d = gg(d, a, b, c, k[2], 9, -51403784);
        c = gg(c, d, a, b, k[7], 14, 1735328473);
        b = gg(b, c, d, a, k[12], 20, -1926607734);
        a = hh(a, b, c, d, k[5], 4, -378558);
        d = hh(d, a, b, c, k[8], 11, -2022574463);
        c = hh(c, d, a, b, k[11], 16, 1839030562);
        b = hh(b, c, d, a, k[14], 23, -35309556);
        a = hh(a, b, c, d, k[1], 4, -1530992060);
        d = hh(d, a, b, c, k[4], 11, 1272893353);
        c = hh(c, d, a, b, k[7], 16, -155497632);
        b = hh(b, c, d, a, k[10], 23, -1094730640);
        a = hh(a, b, c, d, k[13], 4, 681279174);
        d = hh(d, a, b, c, k[0], 11, -358537222);
        c = hh(c, d, a, b, k[3], 16, -722521979);
        b = hh(b, c, d, a, k[6], 23, 76029189);
        a = hh(a, b, c, d, k[9], 4, -640364487);
        d = hh(d, a, b, c, k[12], 11, -421815835);
        c = hh(c, d, a, b, k[15], 16, 530742520);
        b = hh(b, c, d, a, k[2], 23, -995338651);
        a = ii(a, b, c, d, k[0], 6, -198630844);
        d = ii(d, a, b, c, k[7], 10, 1126891415);
        c = ii(c, d, a, b, k[14], 15, -1416354905);
        b = ii(b, c, d, a, k[5], 21, -57434055);
        a = ii(a, b, c, d, k[12], 6, 1700485571);
        d = ii(d, a, b, c, k[3], 10, -1894986606);
        c = ii(c, d, a, b, k[10], 15, -1051523);
        b = ii(b, c, d, a, k[1], 21, -2054922799);
        a = ii(a, b, c, d, k[8], 6, 1873313359);
        d = ii(d, a, b, c, k[15], 10, -30611744);
        c = ii(c, d, a, b, k[6], 15, -1560198380);
        b = ii(b, c, d, a, k[13], 21, 1309151649);
        a = ii(a, b, c, d, k[4], 6, -145523070);
        d = ii(d, a, b, c, k[11], 10, -1120210379);
        c = ii(c, d, a, b, k[2], 15, 718787259);
        b = ii(b, c, d, a, k[9], 21, -343485551);
        x[0] = add32(a, x[0]);
        x[1] = add32(b, x[1]);
        x[2] = add32(c, x[2]);
        x[3] = add32(d, x[3]);
      }

      function cmn(q, a, b, x, s, t) {
        a = add32(add32(a, q), add32(x, t));
        return add32((a << s) | (a >>> (32 - s)), b);
      }

      function ff(a, b, c, d, x, s, t) {
        return cmn((b & c) | ((~b) & d), a, b, x, s, t);
      }

      function gg(a, b, c, d, x, s, t) {
        return cmn((b & d) | (c & (~d)), a, b, x, s, t);
      }

      function hh(a, b, c, d, x, s, t) {
        return cmn(b ^ c ^ d, a, b, x, s, t);
      }

      function ii(a, b, c, d, x, s, t) {
        return cmn(c ^ (b | (~d)), a, b, x, s, t);
      }

      function add32(a, b) {
        return (a + b) & 0xFFFFFFFF;
      }

      function md51(s) {
        const n = s.length;
        const state = [1732584193, -271733879, -1732584194, 271733878];
        let i;
        for (i = 64; i <= s.length; i += 64) {
          md5cycle(state, md5blk(s.substring(i - 64, i)));
        }
        s = s.substring(i - 64);
        const tail = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        for (i = 0; i < s.length; i++) {
          tail[i >> 2] |= s.charCodeAt(i) << ((i % 4) << 3);
        }
        tail[i >> 2] |= 0x80 << ((i % 4) << 3);
        if (i > 55) {
          md5cycle(state, tail);
          for (i = 0; i < 16; i++) tail[i] = 0;
        }
        tail[14] = n * 8;
        md5cycle(state, tail);
        return state;
      }

      function md5blk(s) {
        const md5blks = [];
        for (let i = 0; i < 64; i += 4) {
          md5blks[i >> 2] = s.charCodeAt(i) +
            (s.charCodeAt(i + 1) << 8) +
            (s.charCodeAt(i + 2) << 16) +
            (s.charCodeAt(i + 3) << 24);
        }
        return md5blks;
      }

      const hex_chr = '0123456789abcdef'.split('');

      function rhex(n) {
        let s = '';
        for (let j = 0; j < 4; j++) {
          s += hex_chr[(n >> (j * 8 + 4)) & 0x0F] +
               hex_chr[(n >> (j * 8)) & 0x0F];
        }
        return s;
      }

      function hex(x) {
        for (let i = 0; i < x.length; i++) {
          x[i] = rhex(x[i]);
        }
        return x.join('');
      }

      return hex(md51(string));
    }
  }
};
</script>

<style scoped>
.prose :deep(h1) { @apply text-xl font-bold mb-3 mt-6; }
.prose :deep(h2) { @apply text-lg font-bold mb-3 mt-5; }
.prose :deep(h3) { @apply text-base font-bold mb-2 mt-4; }
.prose :deep(p) { @apply mb-4; }
.prose :deep(ul) { @apply list-disc pl-5 mb-4 space-y-1; }
.prose :deep(ol) { @apply list-decimal pl-5 mb-4 space-y-1; }
.prose :deep(li) { @apply mb-1; }
.prose :deep(code) { @apply bg-gray-100 px-2 py-1 rounded text-sm font-mono; }
.prose :deep(pre) { @apply bg-gray-900 text-gray-100 p-4 rounded text-sm overflow-x-auto mb-4; }
.prose :deep(pre code) { @apply bg-transparent text-gray-100 p-0; }
.prose :deep(strong) { @apply font-bold; }
.prose :deep(em) { @apply italic; }
.prose :deep(a) { @apply text-indigo-600 hover:text-indigo-800 underline; }
.prose :deep(blockquote) { @apply border-l-4 border-gray-300 pl-4 italic text-gray-600 my-4; }
.prose :deep(hr) { @apply border-t-2 border-gray-200 my-6; }

/* Espacement entre les blocs de contenu différents */
.prose :deep(h1 + p),
.prose :deep(h2 + p),
.prose :deep(h3 + p) { @apply mt-2; }
.prose :deep(p + ul),
.prose :deep(p + ol) { @apply mt-3; }
.prose :deep(ul + p),
.prose :deep(ol + p) { @apply mt-3; }
</style>
